/**
 * Playbooks send PRD, Unit U7 — the `gong_engage` content guard (R11, Key
 * Technical Decision 8).
 *
 * A `gong_engage` sequence can never be saved with an EMAIL step that has BOTH
 * an empty subject and an empty body — a rep who clears both fields and saves
 * would silently ship a blank Gong email. An empty SUBJECT alone stays legal
 * (LinkedIn-style steps carry a body and no subject at all), so this is a
 * joint check on the pair, not a per-field one.
 *
 * "EMAIL step" is load-bearing and was added 2026-09-24 (plan
 * `2026-09-24-004-playbooks-gong-nonemail-steps-editable-plan.md`): a Gong
 * flow's Call / LinkedIn / Custom steps are imported and KEPT, every one of
 * them is editable, and a blank one is a LEGAL authored state — blank means
 * "Gong runs its own script for this step" and the send emits no override for
 * it. See {@link isGongEmailStep} below.
 *
 * Deliberately regex-based and DOM-free: `bun:test` runs under `bun`, which
 * has no DOM/`jsdom`, and this module is imported by the plain backend test
 * runner (`saveSequenceSteps`'s handler) as well as by `send-payload.ts`
 * (U3) — neither has a document to parse HTML with. The body field is
 * Gong-authored rich-text HTML (or plain text for a hand-typed step), so
 * "empty" has to mean "no visible content once markup is stripped," not
 * "the raw string is `''`" — a step whose body is `'<p><br></p>'` (a rich
 * editor's empty state) or `'<div>&nbsp;</div>'` is exactly as blank as one
 * whose body is `''`, and both are rejected here. A body that carries an
 * `<img>` is never considered empty, even if its stripped TEXT is empty —
 * an image-only Gong step (a diagram, a screenshot) is real content this
 * rule must not block.
 *
 * This module does NOT touch `SequenceStepSchema` (`./schemas.ts`), which
 * stays deliberately permissive per Key Technical Decision 8 — the bare
 * schema has no channel context (a step's own shape doesn't carry
 * `channel`; that lives on the sequence identity), so the rule can only be
 * enforced where the channel is known: the save-steps HANDLER
 * (`api.ts#saveSequenceSteps`) and the send-payload validator (U3), each
 * importing `validateGongEngageSteps` from here rather than duplicating it.
 */
import { GongFlowStepDetailWireSchema, type GongFlowStepDetailWire } from '../_shared/flow/gong';

import type { LauncherChannel, SequenceStep, SequenceSteps } from './schemas';

/** Matches any HTML tag, opening or closing, self-closing or not. */
const TAG_RE = /<[^>]*>/g;

/**
 * HTML whitespace entities that render as invisible space but survive tag
 * stripping as literal text — most commonly `&nbsp;` (a rich editor's
 * "empty" paragraph often collapses to exactly this), plus its numeric
 * forms and the other named space entities, in case Gong's editor emits
 * one of those instead.
 */
const WHITESPACE_ENTITY_RE = /&(nbsp|ensp|emsp|thinsp|#160|#xa0);?/gi;

/** True for a body containing an `<img …>` tag — never considered empty. */
const HAS_IMG_RE = /<img\b/i;

/** Strips tags and whitespace entities, leaving only visible text (untrimmed). */
function stripToVisibleText(html: string): string {
  return html.replace(TAG_RE, '').replace(WHITESPACE_ENTITY_RE, ' ');
}

function isSubjectEmpty(subject: string | undefined): boolean {
  return (subject ?? '').trim() === '';
}

/**
 * "Body empty" per the doc comment above: the raw string is blank, OR it is
 * HTML whose stripped text content is blank AND it contains no `<img`.
 */
function isBodyEmpty(body: string | undefined): boolean {
  const raw = body ?? '';
  if (raw.trim() === '') return true;
  if (HAS_IMG_RE.test(raw)) return false;
  return stripToVisibleText(raw).trim() === '';
}

// Gong step types, and the narrow set of questions they answer (plan
// `2026-09-24-004-playbooks-gong-nonemail-steps-editable-plan.md`, U1a).
//
// EVERY Gong step is editable in Terra. A Gong flow interleaves email steps
// with Call / LinkedIn Connect / LinkedIn Message / Custom steps, which usually
// arrive blank from the importer (`gongStepsToSequenceSteps`). Those have two
// legal states, and the step type only decides which of them is allowed:
//
//   BLANK (per {@link isContentlessGongStep}) — Gong runs its OWN script for
//   the step. Legal at save and at send, and `send-payload.ts#toOverrideSteps`
//   emits the truly blank `{subject:'', body:'', action_type}` slot that Clay's
//   `Resolve remediated copy` turns into no `step_N_*` key at all, so Gong
//   keeps its task.
//
//   ANY TEXT — the rep's copy replaces Gong's script for the contacts sent.
//   Resolved through the normal token pipeline and sent as a real override,
//   with the SAME copy-defect rules as an email body (an unresolvable `{{…}}`
//   holds that member back).
//
// A blank EMAIL step stays refused at both doors: Gong 400s the whole
// enrollment over a both-empty override, and there is no "Gong runs its own
// email" fallback that makes sense for a rep who cleared one. Before this
// predicate existed the guard below was type-blind and refused every real Gong
// flow carrying a non-email step, which stranded two prod sequences at "bound
// but empty".
//
// The type question is keyed on the step's OWN `gongStepDescriptor.step_type`
// (the importer's snapshot), never on its resolved text — a blank Call step's
// body is Gong's `<div></div>`, not `''`, so "looks empty" is not a usable
// signal at the wire level (see `send-payload.ts#toOverrideSteps`).
//
// {@link isGongEmailStep} is consulted by exactly five things: the two blank
// guards here (blank email refused, blank non-email legal),
// `toOverrideSteps`' blank fold, the Dispatch Inspector's caption
// (`dispatch-inspector-logic.ts#gongRunsStepCaption`), and
// `gong-flow-picker-api.ts#gongStepBodyFormat`. Every token and copy-defect
// scanner is deliberately TYPE-BLIND — a step the rep can write in is a step
// whose tokens the rep needs flagged.

/**
 * Gong step types whose body is unconditionally rich HTML, and whose blank
 * state is a defect rather than a choice. Also the one source
 * `gong-flow-picker-api.ts#gongStepBodyFormat` reads for "this family's body
 * is unconditionally rich HTML" — one email-family set in the app, not two.
 */
export const GONG_EMAIL_STEP_TYPES: ReadonlySet<string> = new Set(['SEND_EMAIL', 'AUTO_SEND_EMAIL']);

/**
 * DERIVED from the closed step-type enum, not hand-listed: a 7th Gong action
 * type lands in here automatically and fails
 * `step-content-rules.test.ts`'s equality pin, so classifying it is a visible
 * decision rather than a silent default.
 *
 * Read off `GongFlowStepDetailWireSchema.shape.step_type` rather than
 * importing `GongFlowStepActionTypeSchema` directly: the enum lives in core
 * (`src/shared/schemas/gong-flow.ts`) and mini-apps reach core wire schemas
 * only through the `_shared/flow/gong` re-export (`mini-apps/CLAUDE.md`,
 * enforced by `.oxlintrc.json`'s `no-restricted-imports` gate), which
 * re-exports the wire step but not the bare enum. Same six values either way
 * — the wire schema's `step_type` IS that enum.
 */
export const GONG_NON_EMAIL_STEP_TYPES: ReadonlySet<string> = new Set(
  GongFlowStepDetailWireSchema.shape.step_type.options.filter(
    (type) => !GONG_EMAIL_STEP_TYPES.has(type),
  ),
);

/**
 * `false` ONLY for a recognised non-email type. `undefined`/non-string (no
 * descriptor — a hand-authored step, or one imported before the descriptor
 * existed) and an unrecognised string are BOTH email: the conservative answer,
 * which keeps today's guard behaviour for every step Terra cannot positively
 * identify as a Gong-run task.
 *
 * `Set.has` walks no prototype chain, so a hostile `step_type` of
 * `'__proto__'` / `'constructor'` / `'toString'` reads as "not a known
 * non-email type" → email, with none of the `hasOwnProperty` ceremony
 * `gongStepTypeLabel`'s object lookup needs.
 */
export function isGongEmailStepType(stepType: unknown): boolean {
  return typeof stepType !== 'string' || !GONG_NON_EMAIL_STEP_TYPES.has(stepType);
}

/** {@link isGongEmailStepType} against a step's own imported descriptor. */
export function isGongEmailStep(step: Pick<SequenceStep, 'gongStepDescriptor'>): boolean {
  return isGongEmailStepType(step.gongStepDescriptor?.step_type);
}

/**
 * Plain-English label per Gong step type, for the step-type chip every editor
 * and preview card shows.
 *
 * LIVES HERE, not in `gong-flow-picker-api.ts` where it was born, and
 * `gong-flow-picker-api.ts` re-exports it so its existing importers are
 * unchanged. The move is a MODULE-GRAPH fix, not a taste one: this file is a
 * DOM-free leaf (zod wire schema + type-only `./schemas`), whereas
 * `gong-flow-picker-api.ts` imports `./mutations` → `./api-client`. When
 * `dispatch-inspector-logic.ts` started reading {@link gongStepTypeLabel} it
 * pulled that whole chain into every test that mounts the Dispatch Inspector,
 * and `dispatch-inspector-clay-render.test.tsx`'s `mock.module('../api-client')`
 * then broke the newly-reached `mutations.ts` import with
 * `SyntaxError: Export named 'archiveIdentityRequest' not found`. A label map
 * has no business dragging a fetch layer behind it.
 *
 * `web/app-home`'s `StepOverrideEditor.tsx` owns the canonical version of this
 * map for Action View's step-override editor — this stays a small, deliberate
 * duplicate, not a shared import: mini-apps may never import `web/app-home`
 * (`mini-apps/CLAUDE.md`), and a six-entry label map behind one optional chip
 * is not worth a `_shared/` module.
 */
export const GONG_STEP_TYPE_LABELS: Record<GongFlowStepDetailWire['step_type'], string> = {
  SEND_EMAIL: 'Manual Email',
  AUTO_SEND_EMAIL: 'Auto Email',
  CALL: 'Call',
  LINKEDIN_CONNECT: 'LinkedIn Connect',
  LINKEDIN_MESSAGE: 'LinkedIn Message',
  CUSTOM: 'Custom Step',
};

/**
 * Reads a plain-English step-type label out of an imported step's
 * `gongStepDescriptor` — `null` when the step carries no descriptor (every
 * non-Gong-imported step) or an unrecognized value (the descriptor is an
 * opaque `z.record`, Key Decision 12 — a future/foreign value must degrade to
 * "no chip", never throw or render garbage).
 */
export function gongStepTypeLabel(descriptor: Record<string, unknown> | undefined): string | null {
  const stepType = descriptor?.step_type;
  // Codex finding 3 fix: `in` walks the prototype chain, so a hostile/
  // foreign `step_type` string naming an `Object.prototype` member (e.g.
  // `'__proto__'`, `'constructor'`, `'toString'`) would pass `stepType in
  // GONG_STEP_TYPE_LABELS` and then read back an INHERITED value instead of
  // `undefined`. `hasOwnProperty.call` checks only this object's own
  // properties — `Object.hasOwn` would do the same but isn't available at
  // this tsconfig's lib target (ES2022+ only).
  return typeof stepType === 'string' &&
    Object.prototype.hasOwnProperty.call(GONG_STEP_TYPE_LABELS, stepType)
    ? GONG_STEP_TYPE_LABELS[stepType as GongFlowStepDetailWire['step_type']]
    : null;
}

/**
 * Subject empty AND body empty once tags / whitespace entities are stripped —
 * exactly the rule {@link validateGongEngageSteps} applies, and THE one
 * definition of "blank" in the app. Four surfaces ask it and must agree:
 * the save guard ({@link validateGongEngageSteps}), the send
 * (`send-payload.ts#toOverrideSteps`' blank fold), the editor's body hint, and
 * the sample preview card.
 *
 * Blank means different things either side of the step-type line, which is
 * why {@link isGongEmailStep} is always asked alongside it. On a NON-EMAIL
 * step blank is a deliberate authored state — "leave this slot alone, Gong
 * runs its own script" — and the send emits no override. On an EMAIL step it
 * is the mistake this rule was built to catch: a rep who cleared both fields
 * would otherwise silently ship a blank Gong email.
 *
 * Strips tags first because Gong stores a blank step's body as `<div></div>`
 * (or a 100-char `data-default-styles` envelope), neither of which is `''`.
 */
export function isContentlessGongStep(step: Pick<SequenceStep, 'subject' | 'body'>): boolean {
  return isSubjectEmpty(step.subject) && isBodyEmpty(step.body);
}

/**
 * What the sample preview card shows for a BLANK non-email Gong step, and the
 * sibling of the editor's own body hint. Leaving a Call / LinkedIn / Custom
 * step empty is a CHOICE, not an unfinished field, and this is the sentence
 * that says what the choice does.
 */
export const GONG_BLANK_STEP_TEXT = 'Left blank — Gong runs its own script for this step.';

/**
 * The caption under a non-email step's Subject field. Subject is AVAILABLE and
 * OPTIONAL on a Call / LinkedIn / Custom step, and never required.
 *
 * Evidence for allowing it: `docs/flow/gong-flow-play-spec-handoff.md:123-125`
 * — "CALL-STEP OVERRIDE ✅ VISUALLY CONFIRMED 2026-08-19 … the override renders
 * as the call's SCRIPT in Gong's dialer — subject = script title, body = script
 * text. Per-step overrides are universal across step types." Corroborated by
 * `src/shared/schemas/gong.ts:201-210` (`GongAssignStepOverrideSchema`), where
 * `subject` and `body` are both optional. No documented Gong rejection of a
 * subject on a non-email step exists; the only subject rejection anywhere is
 * on a REPLY ordinal, which `send-payload.ts#toOverrideSteps` already drops for
 * every step type regardless.
 */
export const NON_EMAIL_SUBJECT_HINT = 'Optional — Gong shows this as the script title.';

/**
 * Positions (ascending, regardless of the input array's own order) of every
 * step in `steps` that is BOTH content-less and Terra's to write. An empty
 * result means the sequence is safe to save as `gong_engage` content; the
 * caller (the save-steps handler, or U3's send-payload validator) decides what
 * "empty" means for a channel — this function only ever runs for
 * `gong_engage`, decided by the caller against the sequence identity's
 * `channel` column.
 *
 * A NON-email step (Call / LinkedIn Connect / LinkedIn Message / Custom, per
 * {@link isGongEmailStep}) is LEGAL blank — Gong runs its own script for it,
 * and the send emits no override: `toOverrideSteps` folds both fields to a
 * truly blank entry so Clay's `Resolve remediated copy` skips the slot. It is
 * equally legal with text, which is sent as a real override. Either way there
 * is nothing here for this guard to report. The joint subject+body check
 * therefore applies to email steps and to steps with no descriptor at all
 * (hand-authored / legacy), which is where the original rule — a rep who
 * clears both fields would silently ship a blank Gong EMAIL — still bites.
 */
export function validateGongEngageSteps(
  steps: readonly Pick<SequenceStep, 'position' | 'subject' | 'body' | 'gongStepDescriptor'>[],
): number[] {
  return steps
    .filter((step) => isGongEmailStep(step) && isContentlessGongStep(step))
    .map((step) => step.position)
    .sort((a, b) => a - b);
}

/** Joins 1+ items into an English list: "a", "a and b", "a, b, and c". */
function joinWithAnd(items: readonly string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0]!;
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

/**
 * The shared 400 `detail` message for `validateGongEngageSteps`'s result —
 * `ordinals` are ALREADY 1-based (QA round 7, U5, item 7): the caller
 * computes each label as `stepOrdinal(sortStepsByPosition(steps), position)`
 * (`gong-flow-picker-api.ts`), the step's 1-based index in the
 * POSITION-SORTED list — NOT `position + 1`, which mislabels a step whenever
 * saved positions have a gap (e.g. `[0, 2]`: the step at position 2 is the
 * SECOND step, ordinal 2, not 3). E.g. "Step 2 and step 5 have no subject and
 * no body — add content or remove the step." for `[2, 5]`, or "Step 2 has no
 * subject and no body — add content or remove the step." for `[2]`.
 */
export function gongEngageEmptyStepsDetail(ordinals: readonly number[]): string {
  const labels = ordinals.map((ordinal) => `step ${ordinal}`);
  const list = joinWithAnd(labels);
  const capitalized = list.charAt(0).toUpperCase() + list.slice(1);
  const verb = ordinals.length === 1 ? 'has' : 'have';
  return `${capitalized} ${verb} no subject and no body — add content or remove the step.`;
}

/**
 * QA round 7, U4 (item 6) — the channels whose first step goes out as an
 * email and therefore needs a subject line. `slack_copilot` sends a message
 * with no subject concept and is deliberately absent (OQ6 in the plan).
 *
 * Typed `ReadonlySet<string>`, not `ReadonlySet<LauncherChannel>`: the call
 * site (`send-payload.ts`) checks a `SequenceIdentityRow.channel`, a raw
 * Prisma scalar (`string`), not a Zod-narrowed `LauncherChannel` — `Set.has`
 * is invariant in its generic, so a `Set<LauncherChannel>` rejects a `string`
 * argument at compile time even though every element in the DB column is a
 * validated channel value at runtime.
 */
export const SUBJECT_REQUIRED_CHANNELS: ReadonlySet<string> = new Set<LauncherChannel>([
  'gong_engage',
  'inflection',
]);

/**
 * `true` when the FIRST step (lowest `position` — the stored array is not
 * guaranteed sorted, `SequenceStepsSchema` pins uniqueness only) has an empty
 * subject AND the bound flow does not send ordinal 1 as a reply. The
 * exception is real, not theoretical: `isReplyAtOrdinal` has no floor and
 * `gong-flow-picker.test.tsx` asserts ordinal 1 CAN be a reply; a reply step
 * carries no subject by design (`toOverrideSteps` drops it), so requiring
 * one would hard-block every flow that opens with a reply. `replyStepOrders`
 * is `null` when the flow shape is unknown (a non-Gong channel, or the mirror
 * has no shape) — then only the subject is consulted. An empty sequence
 * returns `false`: other guards own "no steps at all".
 */
export function firstStepMissingSubject(
  steps: readonly Pick<SequenceStep, 'position' | 'subject'>[],
  replyStepOrders: ReadonlySet<number> | null,
): boolean {
  const first = [...steps].sort((a, b) => a.position - b.position)[0];
  if (first === undefined) return false;
  if (replyStepOrders?.has(1)) return false;
  return isSubjectEmpty(first.subject);
}

/** The rep-facing refusal — one sentence, no code, names the sequence and where to fix it. */
export function firstStepSubjectRequiredDetail(sequenceName: string): string {
  return `Step 1 of "${sequenceName}" needs a subject line before this play can send — add one on the Sequences tab.`;
}

/**
 * The ONE sentence both editors show in place of a Subject field on a Gong
 * reply step (`SequenceStepEditor`'s hand-edit branch and `StepReviewCard`'s
 * Current/Generated columns). Gong 400s the whole enrollment over a subject
 * on a reply step, so the field is not offered at all — and the rep is told
 * WHY, since the prospect still sees a subject line.
 */
export const REPLY_STEP_NO_SUBJECT_TEXT =
  "No subject — Gong sends this step as a reply, so it threads under step 1's subject automatically.";

// QA round 8 (item 6) — every sequence but Gong is born with ONE empty step,
// and Inflection is capped at one step forever after.

/**
 * Channels whose CREATE seeds one empty step so the play-flow editor and
 * "Generate with AI" have a scaffold to build on immediately. `gong_engage`
 * is absent: its steps arrive by IMPORTING a bound flow's real steps
 * (`api.ts#importGongFlowSteps`), never by this seed — seeding one here
 * would just be overwritten (or worse, linger if the flow bind is skipped).
 */
export const SEEDED_STEP_CHANNELS: ReadonlySet<string> = new Set<LauncherChannel>([
  'inflection',
  'slack_copilot',
]);

/**
 * `POST /sequences`'s initial `content.steps` for a freshly-created identity
 * — one empty `{ position: 0, subject: '', body: '' }` for a channel in
 * {@link SEEDED_STEP_CHANNELS}, `[]` for `gong_engage`.
 */
export function seedStepsForChannel(channel: LauncherChannel): SequenceSteps {
  return SEEDED_STEP_CHANNELS.has(channel) ? [{ position: 0, subject: '', body: '' }] : [];
}

/**
 * Inflection sends exactly ONE email: Launcher's `inflection` channel is a
 * single subject+body cell, and the Terra send only ever reads the first
 * override step (`send-payload.ts`'s `firstStep = overrideSteps[0]`) — a
 * second saved step could never be delivered even if it were allowed to
 * exist. Enforced at BOTH save (`api.ts#saveSequenceSteps`, a 400) and send
 * (`send-payload.ts`, a structural `VersioningPlanError`) so a stray second
 * step is refused whichever door it comes through, matching the shape of the
 * `gong_engage` empty-step guard above.
 */
export const INFLECTION_MAX_STEPS = 1;

/**
 * `true` when `channel` is `'inflection'` and `steps` carries more than
 * {@link INFLECTION_MAX_STEPS}. A plain string comparison rather than a
 * `Set.has` on a `LauncherChannel` set (unlike {@link SUBJECT_REQUIRED_CHANNELS}
 * above) because there is exactly one capped channel today — no vocabulary to
 * keep in sync.
 */
export function exceedsInflectionStepLimit(
  channel: string,
  steps: readonly Pick<SequenceStep, 'position'>[],
): boolean {
  return channel === 'inflection' && steps.length > INFLECTION_MAX_STEPS;
}

/**
 * The rep-facing refusal — one sentence, no status code, no error code, names
 * the sequence, why, and WHERE to fix it: the play flow has no step-removal
 * control (U5b adds one to the Library only, QA round 8 Codex fixes).
 */
export function inflectionStepLimitDetail(sequenceName: string): string {
  return `"${sequenceName}" is an Inflection sequence — Inflection sends one email, so it can have only one step. Remove the extra step in the Library before saving.`;
}
